import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  XAxis,
  YAxis
} from 'recharts'
import { useTranslation } from 'react-i18next'

import {
  ANALYTICS_EVENT_TYPES,
  type AnalyticsEventType
} from '@services/event-analytics/constants'
import type {
  AnalyticsGranularity,
  EventAnalyticsTimeseries
} from '@services/event-analytics/types'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig
} from '@/components/ui/chart'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

const chartConfig = {
  count: { label: 'Events', color: '#11adbb' },
  unique_sessions: { label: 'Sessions', color: '#EF946C' }
} satisfies ChartConfig

const ALL_EVENTS_VALUE = '__all__'

interface EventAnalyticsTimeseriesChartProps {
  data?: EventAnalyticsTimeseries
  loading?: boolean
  granularity: AnalyticsGranularity
  onGranularityChange: (granularity: AnalyticsGranularity) => void
  eventFilter: AnalyticsEventType | null
  onEventFilterChange: (event: AnalyticsEventType | null) => void
  disableHourGranularity?: boolean
}

export function EventAnalyticsTimeseriesChart({
  data,
  loading = false,
  granularity,
  onGranularityChange,
  eventFilter,
  onEventFilterChange,
  disableHourGranularity = false
}: EventAnalyticsTimeseriesChartProps) {
  const { t } = useTranslation('eventAnalytics')

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>{t('charts.timeseriesTitle')}</CardTitle>
          <CardDescription>
            {granularity === 'hour'
              ? t('charts.timeseriesDescriptionHour')
              : t('charts.timeseriesDescription')}
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-lg border border-ink/[0.08] p-0.5">
            {(['day', 'hour'] as const).map((value) => {
              const disabled = value === 'hour' && disableHourGranularity
              return (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={granularity === value ? 'default' : 'ghost'}
                  className={cn(
                    'h-8 px-3',
                    granularity !== value && 'hover:bg-transparent',
                    disabled && 'cursor-not-allowed opacity-40'
                  )}
                  disabled={disabled}
                  title={disabled ? t('charts.granularityHourDisabled') : undefined}
                  onClick={() => !disabled && onGranularityChange(value)}
                >
                  {t(`charts.granularity.${value}`)}
                </Button>
              )
            })}
          </div>
          <Select
            value={eventFilter ?? ALL_EVENTS_VALUE}
            onValueChange={(v) =>
              onEventFilterChange(
                v === ALL_EVENTS_VALUE ? null : (v as AnalyticsEventType)
              )
            }
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t('charts.eventFilterAll')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_EVENTS_VALUE}>
                {t('charts.eventFilterAll')}
              </SelectItem>
              {ANALYTICS_EVENT_TYPES.map((eventType) => (
                <SelectItem key={eventType} value={eventType}>
                  {t(`breakdownValues.${eventType}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-[280px] animate-pulse rounded-xl bg-ink/[0.04]" />
        ) : !data?.points?.length ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            {t('eventsTable.empty')}
          </p>
        ) : (
          <ChartContainer config={chartConfig} className="h-[280px] w-full">
            <LineChart data={data.points} margin={{ left: 8, right: 8 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
              />
              <YAxis tickLine={false} axisLine={false} width={40} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                name={t('charts.events')}
                stroke="var(--color-count)"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="unique_sessions"
                name={t('charts.sessions')}
                stroke="var(--color-unique_sessions)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}
